import { useEffect, useState } from 'react'
import type { ConnectionState, RoomError } from './types'
import { useI18n } from './i18n'
import { gotoHome } from './room'

const LS_OPEN = 'tm.shareBar'

/**
 * ルーム画面の最上部に常設する共有バー。
 *
 * このURLを渡せば入れる、という一点だけを伝える。折りたためるが既定は開いている。
 * → doc/sharing/master.md#画面の流れ
 */
export function ShareBar({
  roomId,
  state,
  error,
}: {
  roomId: string
  state: ConnectionState
  error: RoomError | null
}) {
  const { t } = useI18n()
  const [open, setOpen] = useState(() => localStorage.getItem(LS_OPEN) !== 'off')
  const [copied, setCopied] = useState(false)
  const url = `${location.origin}/r/${roomId}`

  useEffect(() => localStorage.setItem(LS_OPEN, open ? 'on' : 'off'), [open])

  useEffect(() => {
    if (!copied) return
    const t = window.setTimeout(() => setCopied(false), 1500)
    return () => clearTimeout(t)
  }, [copied])

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
    } catch {
      // クリップボードが使えない環境（http の別ホストなど）では選択してもらう
      prompt(t('share.prompt'), url)
    }
  }

  return (
    <div className={`sharebar${error ? ' sharebar--error' : ''}`}>
      <div className="sharebar__row">
        <button
          type="button"
          className="icon-btn"
          onClick={() => setOpen(!open)}
          aria-expanded={open}
          title={open ? t('share.close.title') : t('share.open.title')}
        >
          {open ? '▾' : '▸'}
        </button>
        <span className="sharebar__label">{t('share.label')}</span>
        {open && <code className="sharebar__url">{url}</code>}
        <button type="button" className="control" onClick={copy}>
          {copied ? t('share.copied') : t('share.copy')}
        </button>
        <div className="spacer" />
        {error ? (
          <span className="sharebar__error">{t(`roomError.${error}`)}</span>
        ) : (
          state !== 'open' && <span className="sharebar__reconnect">{t('share.reconnect')}</span>
        )}
        <button type="button" className="control" onClick={gotoHome} title={t('share.leave.title')}>
          {t('share.leave')}
        </button>
      </div>
      {open && (
        <p className="sharebar__warn">
          {t('share.warn')}
          <strong>{t('share.warn.strong')}</strong>
          <span className="sharebar__note">{t('share.warn.note')}</span>
        </p>
      )}
    </div>
  )
}
